<script setup>
import InputText from "primevue/inputtext";
import Select from "primevue/select";
import Fieldset from "primevue/fieldset";
import ToggleSwitch from "primevue/toggleswitch";
import Quill from "quill";
import {onMounted} from "vue";
import {getPrettyHTML} from "../../../../helpers.js";
import { trans } from 'laravel-vue-i18n';

let quill = null;
let AlignStyle = Quill.import('attributors/style/align')
let BackgroundStyle = Quill.import('attributors/style/background')
let ColorStyle = Quill.import('attributors/style/color')
let DirectionStyle = Quill.import('attributors/style/direction')
let FontStyle = Quill.import('attributors/style/font')
let SizeStyle = Quill.import('attributors/style/size')

Quill.register(AlignStyle, true);
Quill.register(BackgroundStyle, true);
Quill.register(ColorStyle, true);
Quill.register(DirectionStyle, true);
Quill.register(FontStyle, true);
Quill.register(SizeStyle, true);

onMounted(() => {
    quill = new Quill('#text1', {
        theme: 'snow',
        placeholder: trans('forms.setting_description'),
        modules: {
            toolbar: [
                [{'font': []}, {'size': []}],
                ['bold', 'italic', 'underline', 'strike'],
                [{'color': []}, {'background': []}],
                [{'script': 'super'}, {'script': 'sub'}],
                [{'header': '1'}, {'header': '2'}, 'blockquote'],
                [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
                ['direction', {'align': []}],
                ['link', 'image', 'video', 'formula'],
                ['clean']
            ]
        }
    });
    quill.setContents(JSON.parse(model.value.description_code))
    model.value.description = getPrettyHTML(quill.container.firstChild.innerHTML);
    quill.on('text-change', () => {
        model.value.description_code = JSON.stringify(quill.getContents());
        model.value.description = getPrettyHTML(quill.container.firstChild.innerHTML);
    });
})


const localeOptions = [
    { value: 'cs', label: trans('forms.language_cs') },
    { value: 'en', label: trans('forms.language_en') },
]

const model = defineModel()

const props = defineProps({
    onlyBasics: Boolean
})

const notifications = [
    {
        code: 'newClient',
        name: trans('reservations.notify_new_client'),
        description: trans('reservations.notify_new_client_desc'),
        icon: 'pi-asterisk'
    },
    {
        code: 'confirmClient',
        name: trans('reservations.notify_confirm'),
        description: trans('reservations.notify_confirm_desc'),
        icon: 'pi-thumbs-up'
    },
    {
        code: 'changeClient',
        name: trans('reservations.notify_change'),
        description: trans('reservations.notify_change_desc'),
        icon: 'pi-calendar'
    },
    {
        code: 'cancelClient',
        name: trans('reservations.notify_cancel'),
        description: trans('reservations.notify_cancel_desc'),
        icon: 'pi-calendar-times'
    },
    {
        code: 'reminderClient',
        name: trans('reservations.notify_reminder'),
        description: trans('reservations.notify_reminder_desc'),
        icon: 'pi-bell'
    },
];

const reservationPageSettings = [
    { value: 'off',            label: trans('settings.policy_off') },
    { value: '1 day',          label: trans('settings.policy_1_day') },
    { value: '2 days',         label: trans('settings.policy_2_days') },
    { value: '3 days',         label: trans('settings.policy_3_days') },
    { value: '4 days',         label: trans('settings.policy_4_days') },
    { value: '5 days',         label: trans('settings.policy_5_days') },
    { value: '6 days',         label: trans('settings.policy_6_days') },
    { value: '1 week',         label: trans('settings.policy_1_week') },
    { value: '2 weeks',        label: trans('settings.policy_2_weeks') },
    { value: '1 month',        label: trans('settings.policy_1_month') },
    { value: 'no restriction', label: trans('settings.policy_no_restriction') },
];

const autoApprove = [
    { value: 'Y', label: trans('settings.approve_auto') },
    { value: 'N', label: trans('settings.approve_wait') },
];
</script>

<template>
    <div class="pb-6 pt-1 grid grid-cols-4 gap-4">
        <Fieldset :legend="$t('forms.panel_basic_info')" class="col-span-3">
            <div class="flex mt-2 gap-4">
                <div class="flex flex-col gap-1">
                    <label for="form-name" class="text-sm">{{ $t('forms.label_name') }}</label>
                    <InputText v-model="model.name" id="form-name"></InputText>
                </div>
                <div class="flex flex-col gap-1" style="width: 220px">
                    <label for="form-locale" class="text-sm">{{ $t('forms.label_language') }}</label>
                    <Select v-model="model.language" id="form-name" :placeholder="$t('forms.setting_language_placeholder')" :options="localeOptions"
                            optionLabel="label" optionValue="value"></Select>
                </div>
            </div>
            <div class="flex flex-col gap-1 mt-4">
                <label for="form-name" class="text-sm">{{ $t('forms.setting_description') }}</label>
                <div id="text1"></div>
            </div>
        </Fieldset>
        <Fieldset :legend="$t('forms.panel_reservation_page')">

            <div class="flex flex-col mt-2 gap-2">
                <div class="flex flex-col gap-1">
                    <label for="form-locale" class="text-sm">{{ $t('forms.label_change_policy') }}</label>
                    <Select v-model="model.reservation_change_settings" id="form-reservation_change_settings" :options="reservationPageSettings"
                            optionLabel="label" optionValue="value"></Select>
                </div>
                <div class="flex flex-col gap-1">
                    <label for="form-locale" class="text-sm">{{ $t('forms.label_cancel_policy') }}</label>
                    <Select v-model="model.reservation_cancel_settings" id="form-reservation_cancel_settings" :options="reservationPageSettings"
                            optionLabel="label" optionValue="value"></Select>
                </div>
                <div class="flex flex-col gap-1">
                    <label for="form-locale" class="text-sm">{{ $t('forms.label_change_approve') }}</label>
                    <Select v-model="model.reservation_change_auto_approve" id="form-reservation_change_auto_approve" :options="autoApprove"
                            optionLabel="label" optionValue="value"></Select>
                </div>
                <div class="flex flex-col gap-1">
                    <label for="form-locale" class="text-sm">{{ $t('forms.label_cancel_approve') }}</label>
                    <Select v-model="model.reservation_cancel_auto_approve" id="form-reservation_cancel_auto_approve" :options="autoApprove"
                            optionLabel="label" optionValue="value"></Select>
                </div>
            </div>
        </Fieldset>
        <Fieldset v-if="!onlyBasics" :legend="$t('forms.panel_email_notifications')">
            <div class="flex flex-col gap-2">
                <div v-for="notification in notifications" class="flex gap-3 items-center">
                    <i :class="`pi ${notification.icon} ${model.notifications[notification.code] ? 'text-gray-500' : 'text-gray-300'}`"></i>
                    <ToggleSwitch v-model="model.notifications[notification.code]"/>
                    <div class="flex flex-col">
                        <p>{{ notification.name }}</p>
                        <small>{{ notification.description }}</small>
                    </div>
                </div>
            </div>
        </Fieldset>
    </div>
</template>

<style scoped lang="scss">

</style>
